import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../UserRole/RoleList.css";
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { hasPrivilege } from '../utils/hasPrivilege';
const RoleList = () => {
  const { userData } = useUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [roles, setRoles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [action, setAction] = useState("");
  const [roleName, setRoleName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rolesPerPage, setrowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = localStorage.getItem('auth-token');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const navigate = useNavigate();

  const fetchRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(process.env.REACT_APP_API_ROLE_LIST, {
        headers: {
          'Authorization': `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });


      if (response?.data?.data?.roles && Array.isArray(response.data.data.roles)) {
        setRoles(response?.data?.data?.roles);
      } else {

        setRoles([]);
      }
    } catch (error) {
      setError(error.message);
      setRoles([]);
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const headers = [
    { label: "Role Name", key: "roleName" },
  ];

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleCreateRole = () => {
    navigate("/create-role");
  };

  const handleEditRole = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    if (role) {
      const privileges = role.privileges[0];
      const sectionsAndSubsections = Object.entries(privileges).map(([section, subsections]) => ({
        section,
        subsections
      }));

      navigate("/create-role", {
        state: {
          roleId: role.id,
          roleName: role.roleName,
          sectionsAndSubsections,
        },
      });
    }
  };

  const handleDeleteRole = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    if (role) {
      setSelectedRole(role);
      setAction("delete");
      setIsModalOpen(true);
    }
  };

  const confirmDelete = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_DELETE_ROLE}?roleId=${selectedRole.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        const updatedRoles = roles.filter((role) => role.id !== selectedRole.id);
        setRoles(updatedRoles);
        setIsModalOpen(false);

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Role deleted successfully',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      } else {

        const errorMessage = response.data?.errorMessage || 'Failed to delete the role';

        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      if (error.response) {

        if (error.response.status === 403) {
          Swal.fire({
            icon: 'error',
            title: 'Forbidden',
            text: 'You do not have permission to delete this role.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        } else {

          const errorMessage = error.response.data?.errorMessage || 'An error occurred while deleting the role.';
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMessage,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        }
      } else if (error.request) {

      } else {

      }
      setIsModalOpen(false);
    }
  };

  const handleSaveEdit = () => {
    const updatedRoles = roles.map((role) =>
      role.id === selectedRole.id ? { ...role, name: roleName } : role
    );
    setRoles(updatedRoles);
    setIsModalOpen(false);
  };

  const cancelAction = () => {
    setIsModalOpen(false);
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const filteredRoles = roles.filter((role) =>
    role?.roleName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSort = (columnKey) => {
    let direction = "ascending";
    if (sortConfig.key === columnKey && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key: columnKey, direction });
  };

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;
    return [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === "ascending" ? -1 : 1;
      if (a[key] > b[key]) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  };

  const sortedData = sortData(filteredRoles);
  const indexOfLastRole = currentPage * rolesPerPage;
  const indexOfFirstRole = indexOfLastRole - rolesPerPage;
  const currentRoles = sortedData.reverse().slice(indexOfFirstRole, indexOfLastRole);
  const totalPages = Math.ceil(sortedData.length / rolesPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const totalEntries = filteredRoles.length;
  const indexOfFirstEntry = (currentPage - 1) * rolesPerPage + 1;
  const indexOfLastEntry = Math.min(currentPage * rolesPerPage, totalEntries);

  return (
    <div className="enrollment-container">

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>

        {hasPrivilege(userData, 'USER_ROLE', 'ROLE_LIST_MODIFY') && (
          <button
            className="create-button-new"
            onClick={handleCreateRole}
          >
            <span className="create-button__icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="23"
                height="23"
                className="create-button__svg"
              >
                <path
                  d="M12 5v14M5 12h14"
                  stroke="white"
                  strokeWidth="2.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="create-button__label">
              Create Role
            </span>
          </button>
        )}
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rolesPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>
        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rolesPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={fetchRoles}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredRoles.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="tt-table-container">
        <table className="tt-table">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                        } ${sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
              {hasPrivilege(userData, 'USER_ROLE', 'ROLE_LIST_MODIFY') && (
                <th>Actions</th>)}
            </tr>
          </thead>
          <tbody>
            {currentRoles.map((role) => (
              <tr key={role.id}>
                <td>{role.roleName}</td>
                {hasPrivilege(userData, 'USER_ROLE', 'ROLE_LIST_MODIFY') && (
                  <td className="action-buttons">
                    <button
                      className="edit-btn tooltip-btn"
                      onClick={() => handleEditRole(role.id)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z" />
                      </svg>
                      <span className="tooltip-text">Edit</span>
                    </button>
                    <button
                      className="delete-btn tooltip-btn"
                      onClick={() => handleDeleteRole(role.id)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                      </svg>
                      <span className="tooltip-text">Delete</span>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={prevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={nextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-btn" onClick={cancelAction}>
              &times;
            </button>
            <h2>{action === "edit" ? "Edit Role" : "Delete Role"}</h2>
            {action === "edit" ? (
              <div>
                <label>
                  Role Name:
                  <input
                    type="text"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                  />
                </label>
                <div className="modal-actions">
                  <button className="confirm-btn" onClick={handleSaveEdit}>
                    Save Changes
                  </button>
                  <button className="cancel-btn" onClick={cancelAction}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p>
                  Are you sure you want to delete the role: {selectedRole?.roleName}
                </p>
                <div className="modal-actions">
                  <button className="confirm-btn" onClick={confirmDelete}>
                    Confirm Delete
                  </button>
                  <button className="cancel-btn" onClick={cancelAction}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleList;